/**
 * Mise en forme des valeurs renvoyées par l'API.
 *
 * L'API n'envoie que de l'ISO 8601 UTC et des nombres bruts : tout ce qui est lisible par un
 * humain est calculé ici, dans le fuseau du poste de l'opérateur. Un back-office de supervision
 * affiche donc l'heure locale du STAFF, pas celle de l'exploitation — c'est l'heure à laquelle
 * l'opérateur regarde son écran qui sert de repère pendant un appel de support.
 */

import { FUSEAU_AFFICHAGE } from '../config';
import { VALEUR_ABSENTE } from '../i18n/fr';

const fmtDateHeure = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: FUSEAU_AFFICHAGE,
});

const fmtDateHeureSecondes = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'medium',
  timeZone: FUSEAU_AFFICHAGE,
});

const fmtDate = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeZone: FUSEAU_AFFICHAGE,
});

const fmtHeure = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: FUSEAU_AFFICHAGE,
});

const fmtNombre = new Intl.NumberFormat('fr-FR');

/** Convertit un horodatage ISO en `Date`, ou `null` si la valeur est absente ou invalide. */
export function versDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `30/07/2026 14:03` — repli sur « — » quand la valeur est absente. */
export function formaterDateHeure(iso: string | null | undefined): string {
  const d = versDate(iso);
  return d ? fmtDateHeure.format(d) : VALEUR_ABSENTE;
}

/** `30/07/2026 14:03:07` — pour les journaux, où la seconde compte. */
export function formaterDateHeureSecondes(iso: string | null | undefined): string {
  const d = versDate(iso);
  return d ? fmtDateHeureSecondes.format(d) : VALEUR_ABSENTE;
}

/** `30/07/2026`. */
export function formaterDate(iso: string | null | undefined): string {
  const d = versDate(iso);
  return d ? fmtDate.format(d) : VALEUR_ABSENTE;
}

/** `14:03`. */
export function formaterHeure(iso: string | null | undefined): string {
  const d = versDate(iso);
  return d ? fmtHeure.format(d) : VALEUR_ABSENTE;
}

/**
 * « il y a 12 min », « dans 3 h ». Calculé à partir d'une référence explicite pour rester
 * juste même quand la donnée vient d'un cache : le composant passe l'horloge serveur
 * quand elle est disponible.
 */
export function formaterRelatif(
  iso: string | null | undefined,
  reference: Date = new Date(),
): string {
  const d = versDate(iso);
  if (!d) return VALEUR_ABSENTE;

  const deltaS = Math.round((d.getTime() - reference.getTime()) / 1000);
  const abs = Math.abs(deltaS);
  const passe = deltaS <= 0;

  const rendu = (valeur: number, unite: string, pluriel = 's'): string => {
    const u = valeur > 1 ? `${unite}${pluriel}` : unite;
    return passe ? `il y a ${valeur} ${u}` : `dans ${valeur} ${u}`;
  };

  if (abs < 45) return passe ? 'à l’instant' : 'dans quelques secondes';
  if (abs < 3600) return rendu(Math.round(abs / 60), 'min', '');
  if (abs < 86_400) return rendu(Math.round(abs / 3600), 'h', '');
  if (abs < 30 * 86_400) return rendu(Math.round(abs / 86_400), 'jour');
  if (abs < 365 * 86_400) return rendu(Math.round(abs / (30 * 86_400)), 'mois', '');
  return rendu(Math.round(abs / (365 * 86_400)), 'an');
}

/**
 * Convertit une saisie `datetime-local` (ou tout horodatage) en ISO 8601 UTC.
 *
 * Renvoie `null` plutôt que de lever : `new Date('2026-07-3').toISOString()` jette un
 * `RangeError`. Les filtres de date du journal d'audit vivent dans l'URL, et une URL de
 * back-office se colle dans un ticket, se tronque et se retape à la main — une valeur
 * illisible doit ignorer le filtre, jamais faire tomber l'écran.
 */
export function versIsoUtc(saisie: string | null | undefined): string | null {
  const d = versDate(saisie);
  return d ? d.toISOString() : null;
}

/** `30/07/2026 14:03 (il y a 12 min)` — format des colonnes « dernier heartbeat ». */
export function formaterDateHeureEtRelatif(
  iso: string | null | undefined,
  reference?: Date,
): string {
  const d = versDate(iso);
  if (!d) return VALEUR_ABSENTE;
  return `${fmtDateHeure.format(d)} (${formaterRelatif(iso, reference)})`;
}

/** Durée en secondes → `2 h 05 min`, `45 s`, `3 j 4 h`. */
export function formaterDuree(secondes: number | null | undefined): string {
  if (secondes == null || !Number.isFinite(secondes)) return VALEUR_ABSENTE;
  const s = Math.max(0, Math.round(secondes));
  if (s < 60) return `${s} s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const reste = s % 60;
    return reste === 0 ? `${m} min` : `${m} min ${String(reste).padStart(2, '0')} s`;
  }
  if (s < 86_400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')} min`;
  }
  const j = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  return h === 0 ? `${j} j` : `${j} j ${h} h`;
}

/** Écart entre deux horodatages, en toutes lettres. */
export function formaterEcart(
  debutIso: string | null | undefined,
  finIso: string | null | undefined,
): string {
  const debut = versDate(debutIso);
  const fin = versDate(finIso);
  if (!debut || !fin) return VALEUR_ABSENTE;
  return formaterDuree((fin.getTime() - debut.getTime()) / 1000);
}

/** Séparateur de milliers français. */
export function formaterNombre(valeur: number | null | undefined): string {
  if (valeur == null || !Number.isFinite(valeur)) return VALEUR_ABSENTE;
  return fmtNombre.format(valeur);
}

/** Nombre décimal à décimales fixes, virgule française. */
export function formaterDecimal(valeur: number | null | undefined, decimales = 1): string {
  if (valeur == null || !Number.isFinite(valeur)) return VALEUR_ABSENTE;
  return valeur.toLocaleString('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

/** `8,4 kV`. L'unité vient du nom du champ côté API, jamais de la valeur. */
export function formaterTension(kv: number | null | undefined, decimales = 1): string {
  if (kv == null || !Number.isFinite(kv)) return VALEUR_ABSENTE;
  return `${formaterDecimal(kv, decimales)} kV`;
}

/** `62 %`. */
export function formaterPourcentage(pct: number | null | undefined, decimales = 0): string {
  if (pct == null || !Number.isFinite(pct)) return VALEUR_ABSENTE;
  return `${formaterDecimal(pct, decimales)} %`;
}

/** `-87 dBm`. */
export function formaterDbm(dbm: number | null | undefined): string {
  if (dbm == null || !Number.isFinite(dbm)) return VALEUR_ABSENTE;
  return `${Math.round(dbm)} dBm`;
}

/** `47,382914 ; 0,684201` — 6 décimales, soit ~11 cm, la précision utile d'un GPS de clôture. */
export function formaterCoordonnees(
  lat: number | null | undefined,
  lon: number | null | undefined,
): string {
  if (lat == null || lon == null) return VALEUR_ABSENTE;
  return `${lat.toFixed(6)} ; ${lon.toFixed(6)}`;
}

/** Texte non vide, ou « — ». */
export function ouAbsent(valeur: string | number | null | undefined): string {
  if (valeur == null) return VALEUR_ABSENTE;
  const s = String(valeur).trim();
  return s === '' ? VALEUR_ABSENTE : s;
}

/**
 * Masque un identifiant sensible en n'exposant que ses extrémités : `893315…4471`.
 * L'ICCID complet n'est jamais renvoyé par l'API ; cette fonction sert aux valeurs saisies
 * localement (fichier d'import) qui transitent par l'écran avant envoi.
 */
export function masquer(valeur: string | null | undefined, debut = 6, fin = 4): string {
  if (!valeur) return VALEUR_ABSENTE;
  if (valeur.length <= debut + fin) return valeur;
  return `${valeur.slice(0, debut)}…${valeur.slice(-fin)}`;
}

/** Initiales d'un opérateur, pour la pastille d'identité de la barre supérieure. */
export function initiales(prenom: string, nom: string): string {
  const p = prenom.trim().charAt(0);
  const n = nom.trim().charAt(0);
  return `${p}${n}`.toUpperCase() || '?';
}
